import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const ROLE = { admin: "Administrador", operador: "Operador", leitura: "Leitura" };
const ROLE_BADGE = { admin: "bg-sky-100 text-sky-700", operador: "bg-emerald-100 text-emerald-700", leitura: "bg-slate-100 text-slate-600" };

export default function Utilizadores() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "operador" });
  const [reset, setReset] = useState(null); // user obj for password reset
  const [newPw, setNewPw] = useState("");

  const load = () => api.get("/users").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  if (user?.role !== "admin") {
    return <div className="max-w-md mx-auto bg-white border border-slate-200 rounded-lg p-8 text-center text-slate-500 mt-10" data-testid="utilizadores-sem-acesso">
      <ShieldCheck className="mx-auto mb-2 text-slate-300" size={28} />
      Apenas o administrador pode gerir utilizadores.
    </div>;
  }

  const create = async () => {
    if (!form.name || !form.email || form.password.length < 6) { toast.error("Preencha nome, email e palavra-passe (mín. 6)."); return; }
    try {
      await api.post("/users", form);
      toast.success("Utilizador criado.");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "operador" });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const changeRole = async (u, role) => {
    try { await api.put(`/users/${u.id}`, { role }); toast.success("Perfil atualizado."); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const toggleActive = async (u) => {
    try { await api.put(`/users/${u.id}`, { active: !u.active }); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const doReset = async () => {
    if (newPw.length < 6) { toast.error("Mínimo 6 caracteres."); return; }
    try { await api.put(`/users/${reset.id}`, { password: newPw }); toast.success("Palavra-passe redefinida."); setReset(null); setNewPw(""); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-slate-900">Utilizadores</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-sky-600 hover:bg-sky-700 gap-1.5" data-testid="btn-novo-utilizador"><Plus size={16} /> Novo utilizador</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Novo utilizador</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-nome" /></div>
              <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
              <div><Label className="text-xs">Palavra-passe</Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="mín. 6 caracteres" data-testid="user-password" /></div>
              <div><Label className="text-xs">Perfil</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrador</SelectItem>
                    <SelectItem value="operador">Operador</SelectItem>
                    <SelectItem value="leitura">Leitura</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button className="bg-sky-600 hover:bg-sky-700" onClick={create} data-testid="btn-guardar-utilizador">Criar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
            <tr><th className="text-left px-4 py-2 font-semibold">Nome</th><th className="text-left px-4 py-2 font-semibold">Email</th><th className="text-left px-4 py-2 font-semibold">Perfil</th><th className="text-left px-4 py-2 font-semibold">Estado</th><th className="text-right px-4 py-2 font-semibold">Ações</th></tr>
          </thead>
          <tbody>
            {list.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-2 font-medium text-slate-800">{u.name}</td>
                <td className="px-4 py-2 text-slate-600">{u.email}</td>
                <td className="px-4 py-2">
                  <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                    <SelectTrigger className={`h-7 w-36 text-xs border-0 ${ROLE_BADGE[u.role]}`} data-testid={`user-role-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Administrador</SelectItem>
                      <SelectItem value="operador">Operador</SelectItem>
                      <SelectItem value="leitura">Leitura</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-2">
                  <button onClick={() => toggleActive(u)} data-testid={`user-toggle-${u.id}`} className={`text-[10px] px-2 py-0.5 rounded-full ${u.active !== false ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                    {u.active !== false ? "Ativo" : "Inativo"}
                  </button>
                </td>
                <td className="px-4 py-2 text-right">
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => { setReset(u); setNewPw(""); }} data-testid={`user-reset-${u.id}`}><KeyRound size={12} /> Palavra-passe</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!reset} onOpenChange={(o) => !o && setReset(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Redefinir palavra-passe · {reset?.name}</DialogTitle></DialogHeader>
          <Input type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Nova palavra-passe (mín. 6)" data-testid="reset-pw-input" />
          <DialogFooter><Button variant="outline" onClick={() => setReset(null)}>Cancelar</Button><Button className="bg-sky-600 hover:bg-sky-700" onClick={doReset} data-testid="btn-confirmar-reset">Redefinir</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
